/**
 * KanbanColumn component - displays a column with tasks
 */

import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { GripVertical, MoreHorizontal, Plus } from 'lucide-react';
import TaskCard from './TaskCard';
import type { BoardColumn, Task } from '@services/api';
import type { DraggableAttributes } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';

interface KanbanColumnProps {
  column: BoardColumn;
  tasks: Task[];
  /**
   * Total number of tasks in this column ignoring active board filters.
   * Drives WIP-limit state and the count badge; falls back to `tasks.length`
   * when not provided (no filtering in effect).
   */
  totalCount?: number;
  onTaskClick: (taskId: string) => void;
  onAddTask: () => void;
  onColumnSettings?: () => void;
  showLabels?: boolean;
  showAssignee?: boolean;
  showDueDate?: boolean;
  showPriority?: boolean;
  showSubtaskProgress?: boolean;
  showAttachments?: boolean;
  compact?: boolean;
  // Drag handle props passed from SortableColumn
  dragHandleProps?: DraggableAttributes & SyntheticListenerMap;
  isDragging?: boolean;
}

export default function KanbanColumn({
  column,
  tasks,
  totalCount,
  onTaskClick,
  onAddTask,
  onColumnSettings,
  showLabels = true,
  showAssignee = true,
  showDueDate = true,
  showPriority = true,
  showSubtaskProgress = true,
  showAttachments = true,
  compact = false,
  dragHandleProps,
  isDragging = false,
}: KanbanColumnProps) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    data: { type: 'column', column },
  });

  // WIP limits must be enforced against the full column count, not the
  // filtered/visible subset, otherwise a search/priority filter makes a full
  // column look under-limit and re-enables the disabled "Add task" buttons.
  const columnCount = totalCount ?? tasks.length;
  const isFiltered = totalCount !== undefined && totalCount !== tasks.length;
  const isAtWipLimit = column.wipLimit ? columnCount >= column.wipLimit : false;
  const isOverWipLimit = column.wipLimit ? columnCount > column.wipLimit : false;

  return (
    <div
      className={clsx(
        'flex h-full w-[min(20rem,calc(100vw-2rem))] flex-shrink-0 flex-col rounded-xl border border-neutral-200/80',
        'bg-neutral-100/70 dark:border-neutral-800 dark:bg-neutral-900/50'
      )}
    >
      {/* Column header */}
      <div className="flex items-center justify-between p-3 pb-2">
        <div className="flex items-center gap-2">
          {/* Dedicated drag handle for column reordering (keeps the header's
              buttons out of a single nested interactive control) */}
          {dragHandleProps && (
            <button
              type="button"
              {...dragHandleProps}
              className={clsx(
                'rounded p-1 text-neutral-400 transition-colors hover:text-neutral-600',
                'dark:text-neutral-500 dark:hover:text-neutral-300',
                isDragging ? 'cursor-grabbing' : 'cursor-grab'
              )}
              aria-label={t('board.reorderColumn', 'Reorder column {{name}}', {
                name: column.name,
              })}
            >
              <GripVertical className="h-4 w-4" />
            </button>
          )}
          {/* Color indicator */}
          {column.color && (
            <div
              className="h-3 w-3 rounded-full"
              style={{ backgroundColor: column.color }}
            />
          )}
          <h3 className="font-sans text-sm font-semibold text-neutral-700 dark:text-neutral-200">
            {column.name}
          </h3>
          {/* Task count (full column count drives WIP colouring) */}
          <span
            className={clsx(
              'rounded-full px-2 py-0.5 text-xs font-medium',
              isOverWipLimit
                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                : 'bg-neutral-200 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400'
            )}
          >
            {columnCount}
            {column.wipLimit && `/${column.wipLimit}`}
          </span>
          {/* Filtered indicator: how many of the column's tasks are visible */}
          {isFiltered && (
            <span
              className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
              title={t('board.filteredCountTooltip', '{{shown}} of {{total}} shown by current filters', {
                shown: tasks.length,
                total: columnCount,
              })}
            >
              {t('board.filteredCount', '{{shown}} shown', { shown: tasks.length })}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onAddTask();
            }}
            disabled={isAtWipLimit}
            className={clsx(
              'rounded-md p-2 transition-colors',
              'text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700',
              'dark:hover:bg-neutral-800 dark:hover:text-neutral-300',
              isAtWipLimit && 'cursor-not-allowed opacity-50'
            )}
            title={isAtWipLimit ? t('board.wipLimitReached') : t('board.addTaskTooltip')}
          >
            <Plus className="h-4 w-4" />
          </button>
          {onColumnSettings && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onColumnSettings();
              }}
              className={clsx(
                'rounded-md p-2 transition-colors',
                'text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700',
                'dark:hover:bg-neutral-800 dark:hover:text-neutral-300'
              )}
              title={t('board.columnSettingsTooltip')}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* WIP limit warning */}
      {isOverWipLimit && (
        <div className="mx-3 mb-2 rounded bg-red-100 px-2 py-1 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-400">
          {t('board.wipLimitExceeded')}
        </div>
      )}

      {/* Tasks container */}
      <div
        ref={setNodeRef}
        className={clsx(
          'flex-1 space-y-2 overflow-y-auto p-2',
          'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-neutral-300',
          'dark:scrollbar-thumb-neutral-700',
          isOver && 'bg-primary-50/50 dark:bg-primary-900/10'
        )}
      >
        <SortableContext
          items={tasks.map((t) => t.id)}
          strategy={verticalListSortingStrategy}
        >
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onClick={() => onTaskClick(task.id)}
              showLabels={showLabels}
              showAssignee={showAssignee}
              showDueDate={showDueDate}
              showPriority={showPriority}
              showSubtaskProgress={showSubtaskProgress}
              showAttachments={showAttachments}
              compact={compact}
            />
          ))}
        </SortableContext>

        {/* Empty state */}
        {tasks.length === 0 && (
          <div
            className={clsx(
              'flex h-20 items-center justify-center rounded-lg border border-dashed',
              'border-neutral-300 text-neutral-400',
              'dark:border-neutral-700 dark:text-neutral-600',
              isOver && 'border-primary-300 bg-primary-50/50 dark:border-primary-700'
            )}
          >
            <span className="text-sm">{t('board.dropTasksHere')}</span>
          </div>
        )}
      </div>

      {/* Add task button at bottom */}
      <div className="p-2 pt-0">
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onAddTask();
          }}
          disabled={isAtWipLimit}
          className={clsx(
            'flex w-full items-center justify-center gap-1 rounded-lg py-2',
            'text-sm text-neutral-600 transition-colors',
            'hover:bg-neutral-200 dark:text-neutral-400 dark:hover:bg-neutral-800',
            isAtWipLimit && 'cursor-not-allowed opacity-50'
          )}
        >
          <Plus className="h-4 w-4" />
          <span>{t('board.addTaskButton')}</span>
        </button>
      </div>
    </div>
  );
}
